import moment from "@/utils/moment";

import professionalMixin from "./professionalMixin";
import subscriptionMixin from "./subscriptionMixin";
import ordersMixin from "./orderMixin";
import dateAndTimeMixin from "./generic/dateAndTimeMixin";
import currencyMixin from "./generic/currencyMixin";
import addressMixin from "./addressMixin";

const PAYABLE_STATUSES = [
  "created_on_gateway",
  "payment_requested",
  "waiting_payment"
];

const PAYABLE_VALID_PAY_STATUSES = ["authorized", "settled"];

const PAYABLE_SCHEDULE_EVENT_STATUSES = ["booked"];

const REASONS_FOR_INABILITIES_TO_PAY = {
  cycleAlreadyPaid: {
    title: "Esse ciclo consta como pago.",
    shouldBlockPayment: true
  },
  cycleNotOpen: {
    title: "Esse ciclo ainda não foi aberto para pagamentos!!",
    subtitle: "A abertura dos ciclos acontecem no dia 28 de cada mês.",
    shouldBlockPayment: true
  },
  cycleCancelled: {
    title: "Esse ciclo foi cancelado!",
    subtitle:
      "Um ciclo pode ser cancelado por diversos motivos, como falta de pagamento ou via solicitação do cliente.",
    sugestion:
      "Caso você tenha perdido a data de pagamento ou não solicitou o seu cancelamento, entre em contado com o nosso suporte que resolveremos o seu problema o mais rápido possível.",
    shouldBlockPayment: true
  },
  generic: {
    title: "Existe alguma inconsitência com o ciclo ou os seus pedidos!",
    subtitle:
      "Detectamos algum possível problema com o seu ciclo ou a suas limpezas. Por esse motivo bloqueamos o pagamento para evitar algum erro no valor da sua assinatura.",
    sugestion:
      "Entre em contato com o nosso time de suporte para resolvermos o seu problema o mais rápido possível.",
    shouldBlockPayment: true
  }
};

function isStatusPayable(status) {
  return PAYABLE_STATUSES.includes(status);
}

function isCycleWithOrders(orders) {
  return orders && orders.length > 0;
}

function isOrdersPayables(orders) {
  return orders.every(order => ordersMixin.methods.isOrderPayable(order));
}

function isOrdersEventsValid(orders) {
  return orders.every(order =>
    PAYABLE_SCHEDULE_EVENT_STATUSES.includes(order.schedule_event.status)
  );
}

function isCycleExpired(cycle) {
  return moment()
    .startOf("day")
    .isAfter(moment(cycle.starts_at).startOf("day"));
}

function daysBeforeCycleExpiration(cycle) {
  return moment(cycle.starts_at).diff(moment(), "days");
}

function getPendingBoleto(payments) {
  return payments.find(
    el => el.status === "waiting" && el.funding_instrument.type === "boletos"
  );
}

function haveValidPayment(payments) {
  return (
    payments.length &&
    payments.some(payment =>
      PAYABLE_VALID_PAY_STATUSES.includes(payment.status)
    )
  );
}

// Methods
const isCyclePayable = ({ status, orders }) => {
  return (
    isStatusPayable(status) &&
    isCycleWithOrders(orders) &&
    isOrdersPayables(orders) &&
    isOrdersEventsValid(orders)
  );
};

const getReasonForInabilityToPay = ({ status, orders }) => {
  if (status === "created") {
    return REASONS_FOR_INABILITIES_TO_PAY.cycleNotOpen;
  }

  if (status === "paid") {
    return REASONS_FOR_INABILITIES_TO_PAY.cycleAlreadyPaid;
  }

  if (status === "cancelled") {
    return REASONS_FOR_INABILITIES_TO_PAY.cycleCancelled;
  }

  if (
    !isCycleWithOrders(orders) ||
    !isOrdersPayables(orders) ||
    !isOrdersEventsValid(orders)
  ) {
    return REASONS_FOR_INABILITIES_TO_PAY.generic;
  }
};

const canGoToCheckout = payable => {
  if (getPendingBoleto(payable.payments)) return false;
  if (haveValidPayment(payable.payments)) return false;

  return true;
};

const statesToCheckout = () => {
  return PAYABLE_STATUSES;
};

// Filters
const cycleMonth = cycle => {
  return moment(cycle.starts_at).format("MMMM/YYYY");
};

const cycleOrderCount = cycle => {
  return cycle.orders.length;
};

const cycleOrderStartsAt = cycle => {
  return subscriptionMixin.filters.startTime(cycle.subscription);
};

const cycleOrderDuration = cycle => {
  return ordersMixin.filters.getOrderDuration(cycle.orders[0]);
};

const cycleOrderScheduledDay = order => {
  const scheduleTo = order.schedule_event.starts_at;

  return dateAndTimeMixin.filters.formatDate(scheduleTo, "DD/MM (dddd)");
};

const cycleOrderScheduledStartTime = order => {
  const scheduleTo = order.schedule_event.starts_at;
  const time = dateAndTimeMixin.filters.formatDate(scheduleTo, "HH");

  return `${time}h`;
};

const cycleTotal = cycle => {
  return currencyMixin.filters.toCurrency(cycle.amount.total);
};

const professionalProfilePicture = cycle => {
  return professionalMixin.filters.professionalProfilePicture(
    cycle.professional
  );
};

const professionalFullName = cycle => {
  return professionalMixin.filters.professionalFullName(cycle.professional);
};
const professionalTotalOrders = cycle => {
  return professionalMixin.filters.professionalTotalOrders(cycle.professional);
};

const professionalRating = cycle => {
  return professionalMixin.filters.professionalRating(cycle.professional);
};

const cycleAddressName = cycle => {
  return addressMixin.filters.name(cycle.subscription.address);
};

export default {
  methods: {
    isCyclePayable,
    isCycleExpired,
    daysBeforeCycleExpiration,
    getReasonForInabilityToPay,
    canGoToCheckout,
    statesToCheckout,
    getPendingBoleto
  },
  filters: {
    cycleMonth,
    cycleOrderCount,
    cycleOrderStartsAt,
    cycleOrderDuration,
    cycleOrderScheduledDay,
    cycleOrderScheduledStartTime,
    cycleTotal,
    professionalProfilePicture,
    professionalFullName,
    professionalTotalOrders,
    professionalRating,
    cycleAddressName
  }
};



// WEBPACK FOOTER //
// ./src/mixins/cycleMixin.js